import React from 'react'
import Style from './IncidentFeed.module.css'
import Loading from '../common/LoaderWrapper/loading'
import { formatDetectionDatetime } from '../../utilities/datetime'
import { LOAD_ERROR, NOT_COMPUTED_TITLE, NOT_COMPUTED_NOTE } from './messages'

// ===========================================================================
// Phishing detections ring (PIXMC-6521).
//
// Hand-rolled inline SVG, deliberately NOT one of the three existing doughnut wrappers.
// DoughnutChart.jsx / ChartNestedDoughnut.jsx / DataWithRoundChart.jsx all render to
// <canvas>, so under jsdom there is nothing to query and D-7's three states could not be
// unit-tested at all; none can draw a centre total with rounded caps without new plugins,
// and none can express "no ring at all". The layout idiom (card, ring left, legend right)
// still follows DataWithRoundChart.jsx.
// ===========================================================================

const RADIUS = 33
const CIRCUMFERENCE = 2 * Math.PI * RADIUS // 207.345

// Segment order encodes D-3 precedence, and matches the donut query's CASE order.
// Label note: the wireframe says "Known Phish"; the plan uses "Verified Phish"
// throughout (D-2 / §8 / the PIXMC-6520 field map) and the plan wins.
export const RING_SEGMENTS = [
  { key: 'pixmVerified', label: 'PIXM Verified', color: '#3162a7' },
  { key: 'verifiedPhish', label: 'Verified Phish', color: '#dc2626' },
  { key: 'aiSuspicious', label: 'AI Suspicious', color: '#ca8a04' },
]

const formatCount = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

const PhishingDetectionsRing = ({ overview, loading, error, windowLabel }) => {
  const { available, total, computedAt, lookbackDays } = overview || {}

  // D-7's discriminator. Branch on `available`, NEVER on the counts: 0 is falsy in
  // JavaScript, and a zero ring must only ever mean "we checked and there is nothing".
  // Rendering a clean ring for a customer whose data was never computed would tell an
  // admin their organization is safe when nobody knows.
  //
  // `available` has THREE values, and null is not false:
  //   null  — not asked yet (initial state, before the fetch effect runs)
  //   false — the API answered, and there are no rows for this customer
  //   true  — the API answered with counts
  // Collapsing null into false paints "Data not yet available" for one render on every
  // hard refresh, because the initial state is { available: null, overviewLoading: false }
  // and the effect has not fired yet. That is a sub-frame flicker, but it is exactly the
  // ambiguity D-7 exists to prevent, so null is treated as pending instead.
  const pending = available === null
  const hasData = !loading && available === true

  // The API owns the window length (ticket AC: lookbackDays comes back on the payload so
  // the UI states its own window rather than hard-coding one). Fall back to the window
  // the user picked only while the payload is absent.
  const window = hasData && lookbackDays ? `Last ${lookbackDays} days` : windowLabel

  const safeTotal = total || 0
  let offset = 0

  return (
    <div className={Style.statCard}>
      {/* Fixed-footprint slot: ring when we have data, empty box otherwise, so the card
          does not reflow when a load completes or a sibling swaps in. */}
      <div className={Style.ringChart}>
        {(loading || pending) && <Loading loading={true} />}
        {hasData && (
          <>
            <svg
              viewBox="0 0 80 80"
              role="img"
              aria-label={`Phishing detections: ${formatCount(safeTotal)}`}
            >
              <circle className={Style.ringTrack} cx="40" cy="40" r={RADIUS} />
              {RING_SEGMENTS.map((segment) => {
                const value = overview[segment.key] || 0
                // A zero segment must draw nothing: a rounded cap on a zero-length dash
                // still paints a dot, which would read as "a few" rather than "none".
                if (!safeTotal || !value) return null
                const length = (value / safeTotal) * CIRCUMFERENCE
                const dashOffset = -offset
                offset += length
                return (
                  <circle
                    key={segment.key}
                    data-testid={`ring-segment-${segment.key}`}
                    className={Style.ringSegment}
                    cx="40"
                    cy="40"
                    r={RADIUS}
                    stroke={segment.color}
                    strokeDasharray={`${length} ${CIRCUMFERENCE}`}
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                  />
                )
              })}
            </svg>
            <div className={Style.ringTotal} data-testid="ring-total">
              {formatCount(safeTotal)}
            </div>
          </>
        )}
      </div>

      <div className={Style.statInfo}>
        <div className={Style.statLabel}>Phishing detections</div>
        <div className={Style.statWindow}>{window}</div>

        {hasData ? (
          <>
            <div className={Style.statLegend}>
              {RING_SEGMENTS.map((segment) => (
                <div className={Style.legendItem} key={segment.key}>
                  <span className={Style.legendDot} style={{ background: segment.color }} />
                  {segment.label}
                  <span className={Style.legendVal}>{formatCount(overview[segment.key])}</span>
                </div>
              ))}
            </div>
            {/* Always shown, because it is the only thing distinguishing D-7's "stale"
                state from its "current" state — the counts look identical. */}
            {computedAt && (
              <div className={Style.asOf}>as of {formatDetectionDatetime(computedAt)}</div>
            )}
          </>
        ) : (
          !loading && !pending && (
            <div className={Style.statCardState}>
              {/* A failed load and a never-computed customer are different things and must
                  read differently. Neither ever shows the API's own error text — see
                  messages.js. */}
              <div className={Style.stateTitle}>{error ? LOAD_ERROR : NOT_COMPUTED_TITLE}</div>
              {!error && <div className={Style.stateNote}>{NOT_COMPUTED_NOTE}</div>}
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default PhishingDetectionsRing
